import { type FormEvent, useMemo, useState } from 'react'
import { CheckCircle2 } from 'lucide-react'
import { callSupabaseFunction } from '../../lib/api'
import {
  buildDetailingQuotePayload,
  currentConditionOptions,
  desiredOutcomeOptions,
  detailingQuoteInterestKeys,
  detailingVehicleTypeLabels,
  detailingVehicleTypeOptions,
  formatQuoteContactFollowUp,
  formatSelectedInterestLine,
  includesPaintProtection,
  parseDetailingInterests,
  type DetailingInterestKey,
} from '../../lib/detailing-quote'
import { detailingInterestLabels } from '../../lib/booking-domain'
import { PageShell } from '../layout/page-shell'
import { PageTitle } from '../seo/page-title'
import { Button, ButtonLink } from '../ui/button'

function toggleValue(current: string[], value: string) {
  return current.includes(value) ? current.filter((item) => item !== value) : [...current, value]
}

export function DetailingQuotePage() {
  const initialInterests = useMemo(() => {
    if (typeof window === 'undefined') return []
    return parseDetailingInterests(window.location.search)
  }, [])
  const [serviceInterests, setServiceInterests] = useState<DetailingInterestKey[]>(initialInterests)
  const [desiredOutcomeIds, setDesiredOutcomeIds] = useState<string[]>([])
  const [currentConditionIds, setCurrentConditionIds] = useState<string[]>([])
  const [status, setStatus] = useState<'idle' | 'submitting' | 'sent'>('idle')
  const [message, setMessage] = useState<string | null>(null)
  const [reference, setReference] = useState<string | null>(null)
  const [contactMethod, setContactMethod] = useState('Either')
  const interestLine = formatSelectedInterestLine(serviceInterests)
  const selectedCount = serviceInterests.length
  const showSubmitSummary = selectedCount > 1
  const paintProtectionSelected = includesPaintProtection(serviceInterests)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setMessage(null)
    setStatus('submitting')
    const form = event.currentTarget
    const data = new FormData(form)
    const payload = buildDetailingQuotePayload({
      website: String(data.get('website') ?? ''),
      customer: {
        name: String(data.get('name') ?? ''),
        email: String(data.get('email') ?? ''),
        phone: String(data.get('phone') ?? ''),
        preferredContactMethod: String(data.get('preferredContactMethod') ?? 'Either') as 'Phone' | 'Email' | 'Either',
      },
      vehicle: {
        type: String(data.get('vehicleType') ?? ''),
        make: String(data.get('make') ?? ''),
        model: String(data.get('model') ?? ''),
        year: String(data.get('year') ?? ''),
        registration: String(data.get('registration') ?? ''),
      },
      serviceInterests,
      desiredOutcomeIds,
      currentConditionIds,
      customerNotes: String(data.get('notes') ?? ''),
    })

    try {
      const response = await callSupabaseFunction('quote-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const result = await response.json() as {
        ok?: boolean
        publicReference?: string | null
        error?: string
        fields?: Record<string, string>
      }
      if (!response.ok || !result.ok) {
        const firstFieldError = result.fields ? Object.values(result.fields)[0] : null
        throw new Error(firstFieldError || result.error || 'Unable to submit the quote request.')
      }
      setContactMethod(payload.customer.preferredContactMethod)
      setReference(result.publicReference ?? null)
      setStatus('sent')
    } catch (error) {
      setStatus('idle')
      setMessage(error instanceof Error ? error.message : 'Unable to submit the quote request.')
    }
  }

  return (
    <PageShell className="detailing-quote-page">
      <PageTitle title="Request a Tailored Detailing Quote | Autoglow Detailing" />
      <section className="detailing-quote-section">
        <div className="section-container detailing-quote-layout">
          {status === 'sent' ? (
            <div className="detailing-quote-success" role="status">
              <CheckCircle2 aria-hidden="true" size={42} />
              <h2>Thanks — we’ll review your vehicle and get back to you with a tailored recommendation.</h2>
              {reference ? <p>Enquiry reference <strong>{reference}</strong></p> : null}
              <p>Selected: {interestLine}</p>
              <p>{formatQuoteContactFollowUp(contactMethod)}</p>
              <div className="detailing-quote-success-actions">
                <ButtonLink href="/">Return Home</ButtonLink>
                <ButtonLink href="/gallery" variant="ghost">View Gallery</ButtonLink>
                {paintProtectionSelected ? (
                  <ButtonLink href="/ceramic-protection" variant="ghost">Explore Ceramic Protection</ButtonLink>
                ) : null}
              </div>
            </div>
          ) : (
            <form className="quote-form detailing-quote-form" onSubmit={handleSubmit}>
              {message ? <p className="payment-error" role="alert">{message}</p> : null}
              <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" hidden />

              <header className="detailing-quote-intro">
                <p className="eyebrow">Tailored Quote · Step 2</p>
                <h1>Tell us about your vehicle</h1>
                <p>
                  We’ll review your vehicle, what you’d like to improve, and recommend the right detailing
                  approach.
                </p>
              </header>

              <fieldset className="detailing-quote-group detailing-choice-fieldset">
                <legend>Your detailing interests</legend>
                <p>Carried through from the previous step. Adjust if you need to.</p>
                <div className="detailing-choice-grid">
                  {detailingQuoteInterestKeys.map((key) => {
                    const selected = serviceInterests.includes(key)
                    return (
                      <button
                        aria-pressed={selected}
                        className={selected ? 'detailing-choice-chip is-selected' : 'detailing-choice-chip'}
                        key={key}
                        onClick={() => setServiceInterests((current) => (
                          toggleValue(current, key) as DetailingInterestKey[]
                        ))}
                        type="button"
                      >
                        {detailingInterestLabels[key]}
                      </button>
                    )
                  })}
                </div>
              </fieldset>

              <fieldset className="detailing-quote-group">
                <legend>About you</legend>
                <div className="vehicle-form detailing-quote-about">
                  <label>
                    Full name <span className="required-label">Required</span>
                    <input required name="name" autoComplete="name" />
                  </label>
                  <label>
                    Email <span className="required-label">Required</span>
                    <input required name="email" type="email" autoComplete="email" />
                  </label>
                  <label>
                    Phone <span className="required-label">Required</span>
                    <input required name="phone" type="tel" autoComplete="tel" />
                  </label>
                  <label className="detailing-quote-pref">
                    Preferred contact
                    <select defaultValue="Either" name="preferredContactMethod">
                      <option>Phone</option>
                      <option>Email</option>
                      <option>Either</option>
                    </select>
                  </label>
                </div>
              </fieldset>

              <fieldset className="detailing-quote-group">
                <legend>Your vehicle</legend>
                <div className="vehicle-form">
                  <label>
                    Vehicle type <span className="required-label">Required</span>
                    <select required name="vehicleType" defaultValue="Car">
                      {detailingVehicleTypeOptions.map((option) => (
                        <option key={option} value={option}>{detailingVehicleTypeLabels[option]}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Vehicle make <span className="required-label">Required</span>
                    <input required name="make" autoComplete="off" />
                  </label>
                  <label>
                    Vehicle model <span className="required-label">Required</span>
                    <input required name="model" autoComplete="off" />
                  </label>
                  <label>
                    Vehicle year <span className="optional-label">Optional</span>
                    <input name="year" inputMode="numeric" maxLength={4} />
                  </label>
                  <label>
                    Registration <span className="optional-label">Optional</span>
                    <input name="registration" autoComplete="off" />
                  </label>
                </div>
              </fieldset>

              <fieldset className="detailing-quote-group detailing-choice-fieldset">
                <legend>What would you like improved?</legend>
                <p className="detailing-quote-subhead">Desired outcome</p>
                <p>Choose whatever fits. You can select more than one.</p>
                <div className="detailing-choice-grid">
                  {desiredOutcomeOptions.map((option) => {
                    const selected = desiredOutcomeIds.includes(option.id)
                    return (
                      <button
                        aria-pressed={selected}
                        className={selected ? 'detailing-choice-chip is-selected' : 'detailing-choice-chip'}
                        key={option.id}
                        onClick={() => setDesiredOutcomeIds((current) => toggleValue(current, option.id))}
                        type="button"
                      >
                        {option.label}
                      </button>
                    )
                  })}
                </div>
                <p className="detailing-quote-subhead">Current condition</p>
                <p>A general sense of the vehicle is enough — you don’t need to diagnose the paint.</p>
                <div className="detailing-choice-grid">
                  {currentConditionOptions.map((option) => {
                    const selected = currentConditionIds.includes(option.id)
                    return (
                      <button
                        aria-pressed={selected}
                        className={selected ? 'detailing-choice-chip is-selected' : 'detailing-choice-chip'}
                        key={option.id}
                        onClick={() => setCurrentConditionIds((current) => toggleValue(current, option.id))}
                        type="button"
                      >
                        {option.label}
                      </button>
                    )
                  })}
                </div>
                <label className="wide">
                  Anything else we should know? <span className="optional-label">Optional</span>
                  <textarea
                    name="notes"
                    placeholder="Tell us anything specific about the vehicle, its condition, or what you’d like to achieve."
                    rows={4}
                  />
                </label>
              </fieldset>

              <div className="detailing-quote-submit">
                {showSubmitSummary ? (
                  <p className="detailing-quote-summary">Selected: {interestLine}</p>
                ) : null}
                <Button disabled={status === 'submitting'} type="submit">
                  {status === 'submitting' ? 'Sending request…' : 'Request a Tailored Quote'}
                </Button>
                {paintProtectionSelected ? (
                  <p className="detailing-ceramic-note">
                    <a href="/ceramic-protection">Explore Ceramic Protection</a>
                  </p>
                ) : null}
              </div>
            </form>
          )}
        </div>
      </section>
    </PageShell>
  )
}

export default DetailingQuotePage
